import { InputAdornment, Theme, Typography } from "@mui/material"
import TextField from "@mui/material/TextField"
import { isNil } from "lodash"
import { useState } from "react"
import { FormattedNumber, IntlShape, useIntl } from "react-intl"
import { NumericFormat } from "react-number-format"
import { DateFormats, PlaneusLanguages } from "~/enums/languages"
import { Nullable, Optional } from "~/types/GenericTypes"
import { formatDecimal } from "~/utils/decimal"
import { EXAMPLE_NUMBER } from "~/constants/consts"

type DecimalV2Value = Nullable<number | string>

interface DecimalV2Props {
  label: string
  labelColor?: string
  required?: boolean
  input: {
    value: DecimalV2Value
    onChange: (value: DecimalV2Value) => void
    onBlur?: (value: DecimalV2Value) => void
    name?: string
  }
  meta?: {
    touched?: boolean
    error?: string
  }
  disableUnderline?: boolean
  hideAdornment?: boolean
  hideMinAdornment?: boolean
  onlyInteger?: boolean
  min?: number
  max?: number
  helperText?: string | React.ReactNode
  fontSize?: string | number
}

const validate = (
  value: DecimalV2Value,
  required: boolean,
  min: Optional<number>,
  max: Optional<number>,
  intl: IntlShape
): Optional<string> => {
  if (value === null || value === undefined || value === "") {
    return required ? intl.formatMessage({ id: "validations.required" }) : undefined
  }

  if (!isNil(min) && (typeof value === "number" ? value < min : parseFloat(value) < min)) {
    return intl.formatMessage({ id: "validations.atLeast" }, { value: formatDecimal(min, intl) })
  }
  if (!isNil(max) && (typeof value === "number" ? value > max : parseFloat(value) > max)) {
    return intl.formatMessage({ id: "validations.atMost" }, { value: formatDecimal(max, intl) })
  }
  return undefined
}

const getNumberFormatLocale = (locale: string) => {
  if (locale === PlaneusLanguages.CZ) {
    return DateFormats.CS_CZ
  }
  return locale
}

const getNumberSeparators = (locale: string, value: DecimalV2Value) => {
  const formatter = new Intl.NumberFormat(getNumberFormatLocale(locale))
  const seperatorParts = formatter.formatToParts(EXAMPLE_NUMBER)
  const decimalSeparator = seperatorParts.find(seperator => seperator.type === "decimal")?.value ?? "."
  const groupSeparator = seperatorParts.find(seperator => seperator.type === "group")?.value ?? false

  const numericValue = typeof value === "number" ? value : Number(value)
  const hasGroupSeparator =
    Number.isFinite(numericValue) && formatter.formatToParts(Math.abs(numericValue)).some(part => part.type === "group")

  return {
    thousandSeparator: Number.isFinite(numericValue) && !hasGroupSeparator ? false : groupSeparator,
    decimalSeparator,
  }
}

const DecimalV2: React.FC<DecimalV2Props> = ({
  label,
  labelColor,
  onlyInteger = false,
  required = false,
  disableUnderline = false,
  hideAdornment = false,
  hideMinAdornment = false,
  input: { value, onChange, onBlur },
  meta,
  min,
  max,
  helperText,
  fontSize,
}: DecimalV2Props) => {
  const intl = useIntl()
  const { locale } = intl
  const [touched, setTouched] = useState(false)

  const showError = meta?.touched ?? touched
  const error = showError && validate(value, required, min, max, intl)

  const { thousandSeparator, decimalSeparator } = getNumberSeparators(locale, value)
  const showMinAdornment = !hideMinAdornment && !isNil(min)
  const showMaxAdornment = !isNil(max)
  const showAdornment = !hideAdornment && (showMinAdornment || showMaxAdornment)

  const handleBlur = () => {
    setTouched(true)
    if (onBlur) {
      onBlur(value)
    }
  }

  return (
    <NumericFormat
      customInput={TextField}
      required={required}
      error={!!error}
      helperText={error || helperText}
      InputLabelProps={{
        sx: { color: labelColor },
      }}
      FormHelperTextProps={{
        sx: { color: labelColor },
      }}
      InputProps={{
        disableUnderline,
        sx: {
          fontSize,
          color: labelColor,
          "& .MuiInputBase-input": { color: labelColor },
          "&:before": { borderBottomColor: labelColor },
          "&:after": { borderBottomColor: labelColor },
          "&:hover:not(.Mui-disabled):before": { borderBottomColor: labelColor },
          "&.Mui-focused:after": { borderBottomColor: (theme: Theme) => theme.palette.primary.main },
        },
        endAdornment: showAdornment && (
          <InputAdornment position="end">
            <Typography color={labelColor} component="span" noWrap variant="body2">
              {showMinAdornment && (
                <>
                  min: <FormattedNumber value={min} />
                </>
              )}
              {showMinAdornment && showMaxAdornment && " "}
              {showMaxAdornment && (
                <>
                  max: <FormattedNumber value={max} />
                </>
              )}
            </Typography>
          </InputAdornment>
        ),
      }}
      min={min}
      max={max}
      variant="standard"
      decimalScale={onlyInteger ? 0 : 2}
      value={value}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      allowNegative
      onBlur={handleBlur}
      onValueChange={values => {
        onChange(values.floatValue ?? null)
      }}
      label={label}
      fullWidth
    />
  )
}

export default DecimalV2
